// ════════════════════════════════════════════════════════════════════════════
//  21_LeaderboardPage.jsx  —  MINIMALIST SAṄKṢEPA RĀMĀYAṆA LEADERBOARD
//  Top 5 who attended all 17 quizzes with top score, followed by the rest in order.
//  Clean minimalist design, Tabler icons, NO EMOJIS.
// ════════════════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, F } from './01_theme';
import { TOP_SCORERS } from './leaderboard-data';

export function LeaderboardPage({ setActivePage }) {
  const navigate = useNavigate();

  // Search query
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination display count for list below top 5
  const [displayCount, setDisplayCount] = useState(20);

  // Full master list sorted by:
  // 1. Attempts descending (17 quizzes first, then 16, 15, ...)
  // 2. Average score descending
  // 3. Perfect scores (centums) descending
  const masterList = useMemo(() => {
    return [...TOP_SCORERS].sort((a, b) => {
      if (b.attempts !== a.attempts) return b.attempts - a.attempts;
      if (b.avg !== a.avg) return b.avg - a.avg;
      return (b.centums || 0) - (a.centums || 0);
    });
  }, []);

  // Filtered list when searching
  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return masterList;
    const q = searchQuery.toLowerCase().trim();
    return masterList.filter((p) => p.name.toLowerCase().includes(q));
  }, [masterList, searchQuery]);

  // Top 5 who attended all 17 quizzes with the highest score
  const top5Champions = useMemo(() => {
    return masterList.filter((p) => p.attempts === 17).slice(0, 5);
  }, [masterList]);

  // Ranks 6 onwards in the same order
  const remainingList = useMemo(() => {
    return masterList.slice(5, 5 + displayCount);
  }, [masterList, displayCount]);

  // Single spotlight search match
  const searchSpotlight = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return null;
    const q = searchQuery.toLowerCase().trim();
    const match = masterList.find((p) => p.name.toLowerCase().includes(q));
    if (!match) return null;
    const rank = masterList.findIndex((p) => p.email === match.email) + 1;
    return { ...match, rank };
  }, [masterList, searchQuery]);

  return (
    <div style={{
      minHeight: '100vh',
      background: C.pageBg,
      padding: 'clamp(20px, 3.5vw, 36px) 16px 80px',
      color: C.ink,
      fontFamily: F.body,
    }}>
      <style>{`
        @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');
        .lb-row:hover {
          background: #FFFDF8 !important;
          border-color: #E2D1BE !important;
        }
      `}</style>

      <div style={{ maxWidth: 880, margin: '0 auto' }}>

        {/* ── TOP BREADCRUMB NAVIGATION ─────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <button
            onClick={() => {
              if (setActivePage) setActivePage('Daily Satsaṅga');
              navigate('/dailysatsanga');
            }}
            style={{
              background: 'none',
              border: `1px solid ${C.inkGhost}`,
              borderRadius: 20,
              padding: '6px 14px',
              color: C.inkLight,
              fontSize: 13,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = C.inkGhost; e.currentTarget.style.color = C.inkLight; }}
          >
            <i className="ti ti-arrow-left" style={{ fontSize: 14 }} /> Back to Daily Satsaṅga
          </button>

          <span style={{
            fontSize: 12,
            fontWeight: 600,
            color: C.accent,
            background: C.accentBg,
            padding: '3px 12px',
            borderRadius: 14,
            border: `1px solid ${C.accentSoft}`
          }}>
            17 Weeks Assessment
          </span>
        </div>

        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: '#FFF4E8',
            border: '1px solid #F5D3B3',
            padding: '4px 12px',
            borderRadius: 16,
            marginBottom: 10,
            fontSize: 11.5,
            fontWeight: 700,
            color: C.accent,
            letterSpacing: 0.8,
            textTransform: 'uppercase'
          }}>
            <i className="ti ti-trophy" style={{ fontSize: 14 }} /> SAṄKṢEPA RĀMĀYAṆA
          </div>

          <h1 style={{
            fontFamily: F.hero,
            fontSize: 'clamp(26px, 3.5vw, 36px)',
            color: C.ink,
            margin: '0 0 8px',
            lineHeight: 1.2,
          }}>
            Leaderboard
          </h1>

          <p style={{
            fontSize: 'clamp(13px, 1.2vw, 15px)',
            color: C.inkLight,
            maxWidth: 580,
            margin: '0 auto',
            lineHeight: 1.5,
          }}>
            Celebrating learners who attended all 17 weekly quizzes with top scores, followed by overall course rankings.
          </p>
        </div>

        {/* ── SEARCH BAR ──────────────────────────────────────────────── */}
        <div style={{
          position: 'relative',
          maxWidth: 500,
          margin: '0 auto 28px',
        }}>
          <div style={{
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            color: C.inkLight,
            fontSize: 16,
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
          }}>
            <i className="ti ti-search" />
          </div>

          <input
            type="text"
            placeholder="Search participant by name..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setDisplayCount(20); }}
            style={{
              width: '100%',
              padding: '11px 38px 11px 40px',
              borderRadius: 24,
              border: `1.5px solid ${searchQuery ? C.accent : C.inkGhost}`,
              fontSize: 14,
              outline: 'none',
              fontFamily: F.body,
              background: '#FFFFFF',
              boxShadow: '0 2px 8px rgba(42,26,10,0.04)',
              transition: 'border-color 0.2s ease',
              boxSizing: 'border-box',
            }}
          />

          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: C.inkLight,
                fontSize: 16,
                padding: 4,
                display: 'flex',
                alignItems: 'center',
              }}
              title="Clear search"
            >
              <i className="ti ti-x" />
            </button>
          )}
        </div>

        {/* ── SEARCH SPOTLIGHT CARD (IF ACTIVE SEARCH) ────────────────── */}
        {searchSpotlight && (
          <div style={{
            background: '#FFFFFF',
            border: `1.5px solid ${C.accentSoft}`,
            borderRadius: 14,
            padding: '14px 20px',
            marginBottom: 24,
            boxShadow: '0 4px 16px rgba(200,85,26,0.08)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Search Result
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, marginTop: 2 }}>
                {searchSpotlight.name}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: C.inkLight }}>Overall Rank</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>
                  #{searchSpotlight.rank}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: C.inkLight }}>Average Score</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
                  {searchSpotlight.avg} / 7
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: C.inkLight }}>Quizzes Taken</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
                  {searchSpotlight.attempts} of 17
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: C.inkLight }}>Centums</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32' }}>
                  {searchSpotlight.centums || 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── TOP 5: ALL 17 QUIZZES WITH TOP SCORE ─────────────────────── */}
        {!searchQuery && (
          <div style={{ marginBottom: 32 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 14,
              color: C.ink,
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: 0.3,
            }}>
              <i className="ti ti-crown" style={{ color: '#D4AF37', fontSize: 18 }} />
              <span>Top 5 Champions · 17/17 Quizzes Attended</span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))',
              gap: 12,
            }}>
              {top5Champions.map((p, idx) => {
                const rank = idx + 1;

                return (
                  <div
                    key={idx}
                    style={{
                      background: '#FFFFFF',
                      border: `1px solid ${C.inkGhost}`,
                      borderRadius: 14,
                      padding: '14px 10px',
                      textAlign: 'center',
                      boxShadow: '0 2px 8px rgba(42,26,10,0.03)',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      {/* Rank Icon Pill */}
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                        padding: '3px 9px',
                        borderRadius: 12,
                        background: '#FFF4E8',
                        color: C.accent,
                        border: '1px solid #F5D3B3',
                        fontSize: 12,
                        fontWeight: 700,
                        margin: '0 auto 8px',
                      }}>
                        <i className="ti ti-award" style={{ fontSize: 13 }} />
                        <span>#{rank}</span>
                      </div>

                      {/* Participant Name */}
                      <div style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: C.ink,
                        marginBottom: 6,
                        lineHeight: 1.3,
                        minHeight: 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {p.name}
                      </div>
                    </div>

                    <div>
                      {/* Average Score */}
                      <div style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: C.accent,
                        marginBottom: 3,
                      }}>
                        {p.avg} / 7 Avg
                      </div>

                      {/* Quizzes & Centums */}
                      <div style={{ fontSize: 11, color: C.inkLight, lineHeight: 1.3 }}>
                        <div>17 of 17 Weeks</div>
                        <div style={{ color: '#2E7D32', fontWeight: 600, marginTop: 1 }}>
                          {p.centums} Centums
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── REMAINING LIST IN THE SAME ORDER ─────────────────────────── */}
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
            color: C.inkLight,
            fontSize: 13,
            fontWeight: 600,
          }}>
            <span>
              {searchQuery
                ? `Search Results (${filteredList.length} found)`
                : 'All Participants (In Ranked Order)'}
            </span>
            <span style={{ fontSize: 11.5, color: C.inkLight, fontWeight: 400 }}>
              Ranked by Attendance & Top Score
            </span>
          </div>

          <div style={{
            background: '#FFFFFF',
            border: `1px solid ${C.inkGhost}`,
            borderRadius: 14,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(42,26,10,0.03)',
          }}>
            {(searchQuery ? filteredList.slice(0, displayCount) : remainingList).map((p, idx) => {
              // Calculate continuous rank:
              // If searching, find index in masterList + 1
              // If not searching, starts at rank 6
              const rank = searchQuery
                ? masterList.findIndex((m) => m.email === p.email) + 1
                : idx + 6;
              const isAllStar = p.attempts === 17;

              return (
                <div
                  key={idx}
                  className="lb-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 18px',
                    borderBottom: `1px solid ${C.surfaceAlt}`,
                    transition: 'background 0.15s ease',
                    gap: 12,
                  }}
                >
                  {/* Left: Rank & Name */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flex: 1 }}>
                    <div style={{
                      width: 28,
                      height: 28,
                      borderRadius: 14,
                      background: rank <= 5 ? '#FFF4E8' : C.surface,
                      color: rank <= 5 ? C.accent : C.inkLight,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>
                      {rank}
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: C.ink,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {p.name}
                      </div>

                      {isAllStar && (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          fontSize: 11,
                          color: '#2E7D32',
                          fontWeight: 500,
                          marginTop: 1,
                        }}>
                          <i className="ti ti-check" style={{ fontSize: 12 }} /> 17/17 Weeks Complete
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Scores & Attempts */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: C.accent,
                    }}>
                      {p.avg} / 7 Avg
                    </div>
                    <div style={{ fontSize: 11.5, color: C.inkLight, marginTop: 1 }}>
                      {p.attempts} of 17 Weeks · {p.centums || 0} centums
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredList.length === 0 && (
              <div style={{ padding: '36px 20px', textAlign: 'center', color: C.inkLight, fontSize: 14 }}>
                No participants found matching &ldquo;{searchQuery}&rdquo;.
              </div>
            )}
          </div>

          {/* ── SHOW MORE BUTTON ─────────────────────────────────────── */}
          {(searchQuery ? filteredList.length : masterList.length - 5) > displayCount && (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button
                onClick={() => setDisplayCount((prev) => prev + 25)}
                style={{
                  background: '#FFFFFF',
                  border: `1px solid ${C.inkGhost}`,
                  borderRadius: 20,
                  padding: '8px 22px',
                  color: C.inkMid,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                  transition: 'all 0.15s ease',
                }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = C.inkGhost; e.currentTarget.style.color = C.inkMid; }}
              >
                Show More <i className="ti ti-chevron-down" style={{ fontSize: 14 }} />
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
